import React, { useLayoutEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, KeyRound, Loader2 } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SEO } from "@/components/seo/SEO";
import { api } from "@/services/api/client";
import {
  getPublicAuthChallengeFailureMessage,
  getPublicAuthChallengeToken,
  isPublicAuthChallengeFailure,
} from "@/services/api/publicAuthChallenge";
import type {
  ApiResponse,
  ResetPasswordRequest,
  ResetPasswordSuccessResponse,
} from "@/types";

type ResetPasswordState =
  | "ready"
  | "missing-token"
  | "invalid-token"
  | "submitting"
  | "reset"
  | "failed"
  | "challenge-failed";

const PASSWORD_RESET_TOKEN_PATTERN = /^[a-f0-9]{64}$/i;
const RESET_PASSWORD_FAILURE_MESSAGE =
  "This reset link could not be confirmed. It may be expired or already used. Request a new link to continue.";

const isResetPasswordSuccessResponse = (
  response: ApiResponse<ResetPasswordSuccessResponse>,
): boolean =>
  response.success === true &&
  response.data?.reset === true &&
  response.data.status === "reset";

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(
    () => searchParams.get("token")?.trim() ?? "",
    [searchParams],
  );
  const initialState: ResetPasswordState = token
    ? PASSWORD_RESET_TOKEN_PATTERN.test(token)
      ? "ready"
      : "invalid-token"
    : "missing-token";
  const [resetState, setResetState] =
    useState<ResetPasswordState>(initialState);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState("");

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !searchParams.has("token")) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("token");
    window.history.replaceState(
      window.history.state,
      document.title,
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, [searchParams]);

  const isTokenUsable = PASSWORD_RESET_TOKEN_PATTERN.test(token);
  const isSubmitting = resetState === "submitting";

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidationError("");

    if (!token) {
      setResetState("missing-token");
      return;
    }

    if (!isTokenUsable) {
      setResetState("invalid-token");
      return;
    }

    if (password.length < 8) {
      setValidationError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setValidationError("Passwords must match.");
      return;
    }

    setResetState("submitting");

    try {
      const publicAuthChallengeToken =
        await getPublicAuthChallengeToken("reset-password");
      const response = await api.post<
        ResetPasswordSuccessResponse,
        ResetPasswordRequest
      >(
        "/auth/reset-password",
        publicAuthChallengeToken
          ? {
              token,
              password,
              confirmPassword,
              publicAuthChallengeToken,
            }
          : { token, password, confirmPassword },
      );

      setResetState(
        isResetPasswordSuccessResponse(response) ? "reset" : "failed",
      );
      setPassword("");
      setConfirmPassword("");
    } catch (error) {
      setResetState(
        isPublicAuthChallengeFailure(error) ? "challenge-failed" : "failed",
      );
    }
  };

  const content = (() => {
    switch (resetState) {
      case "reset":
        return {
          icon: <CheckCircle2 className="h-5 w-5 text-emerald-600" />,
          title: "Password Reset",
          description: "Your password has been updated.",
          body: (
            <div className="space-y-4">
              <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="rounded-md border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400"
              >
                Your password has been reset. Sign in with your new password to
                continue.
              </div>
              <Button asChild className="w-full">
                <Link to="/login">Sign in</Link>
              </Button>
            </div>
          ),
        };
      case "missing-token":
      case "invalid-token":
        return {
          icon: <AlertCircle className="h-5 w-5 text-destructive" />,
          title: "Reset Link Needed",
          description: "Request a new password reset link.",
          body: (
            <div className="space-y-4">
              <div
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {resetState === "missing-token"
                  ? "This reset link is missing required information."
                  : RESET_PASSWORD_FAILURE_MESSAGE}
              </div>
              <Button asChild className="w-full">
                <Link to="/forgot-password">Request reset link</Link>
              </Button>
              <Button asChild variant="ghost" className="w-full">
                <Link to="/login">Back to sign in</Link>
              </Button>
            </div>
          ),
        };
      case "failed":
      case "challenge-failed":
      case "ready":
      case "submitting":
      default:
        return {
          icon: <KeyRound className="h-5 w-5 text-primary" />,
          title: "Set New Password",
          description: "Choose a new password for your account.",
          body: (
            <form onSubmit={handleSubmit} className="space-y-4">
              {(resetState === "failed" ||
                resetState === "challenge-failed" ||
                validationError) && (
                <div
                  role="alert"
                  aria-live="assertive"
                  aria-atomic="true"
                  className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {validationError ||
                    (resetState === "challenge-failed"
                      ? getPublicAuthChallengeFailureMessage()
                      : RESET_PASSWORD_FAILURE_MESSAGE)}
                </div>
              )}
              <div className="space-y-2">
                <label
                  htmlFor="reset-password-new-password"
                  className="text-sm font-medium"
                >
                  New password
                </label>
                <Input
                  id="reset-password-new-password"
                  type="password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setValidationError("");
                    if (
                      resetState === "failed" ||
                      resetState === "challenge-failed"
                    ) {
                      setResetState("ready");
                    }
                  }}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="reset-password-confirm-password"
                  className="text-sm font-medium"
                >
                  Confirm password
                </label>
                <Input
                  id="reset-password-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => {
                    setConfirmPassword(event.target.value);
                    setValidationError("");
                    if (
                      resetState === "failed" ||
                      resetState === "challenge-failed"
                    ) {
                      setResetState("ready");
                    }
                  }}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={
                  isSubmitting || password === "" || confirmPassword === ""
                }
              >
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Reset password
              </Button>
              <Button asChild variant="ghost" className="w-full">
                <Link to="/login">Back to sign in</Link>
              </Button>
            </form>
          ),
        };
    }
  })();

  return (
    <>
      <SEO
        title="Set a New TerraMatrix Password"
        description="Set a new password for your TerraMatrix account."
        canonical="/reset-password"
        noindex={true}
      />
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            {content.icon}
            <CardTitle>{content.title}</CardTitle>
          </div>
          <CardDescription>{content.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">{content.body}</CardContent>
      </Card>
    </>
  );
};
